<template>
  <q-page class="q-px-xl q-pt-xl centered-container">
    <div class="row items-start justify-between q-mb-lg">
      <div class="col">
        <div class="text-h4 q-mb-xs">
          {{ i18n.title }}
        </div>
        <div class="text-body2 text-secondary">
          {{ i18n.subtitle }}
        </div>
        <div class="text-caption text-grey-6 q-mt-sm">
          <span v-if="auditData.completed_at">
            {{ i18n.last_audit }} : {{ formatDate(auditData.completed_at) }}
          </span>
          <span v-else>
            {{ auditStatusText }}
          </span>
        </div>
      </div>
      <div
        v-if="auditStatus !== 'idle'"
        class="col-auto row items-center q-gutter-sm"
      >
        <q-btn
          color="primary"
          :loading="auditStatus === 'running'"
          :percentage="auditProgress"
          no-caps
          unelevated
          :disable="starting && auditStatus !== 'running'"
          :icon="auditStatus === 'running' ? 'hourglass_top' : 'auto_awesome'"
          :label="auditStatus === 'running' ? i18n.audit_running : i18n.start_audit"
          @click="startAudit"
        />
        <q-btn
          v-if="auditStatus === 'running' || auditStatus === 'pending'"
          color="negative"
          outline
          no-caps
          unelevated
          :loading="cancelling"
          icon="stop_circle"
          :label="i18n.cancel_audit"
          @click="confirmCancelAudit"
        />
      </div>
    </div>

    <div
      v-if="auditStatus === 'failed'"
      class="q-mb-lg"
    >
      <q-banner
        class="bg-negative text-default q-pa-md"
        rounded
      >
        <template v-slot:avatar>
          <q-icon
            name="error"
            color="white"
          />
        </template>
        {{ formattedError }}
      </q-banner>
    </div>

    <div class="q-mb-xl">
      <div v-if="sortedChecks.length > 0">
        <q-list class="pt-4">
          <template
            v-for="(check, index) in sortedChecks"
            :key="check.name"
          >
            <q-expansion-item
              v-if="check.status !== 'passed'"
              class="my-4"
              :icon="getCheckIcon(check.status)"
              :header-class="getCheckHeaderClass(check.status)"
            >
              <template v-slot:header>
                <q-item-section avatar>
                  <q-avatar
                    size="32px"
                    :color="getCheckColor(check.status)"
                  >
                    <q-icon
                      :name="getCheckIcon(check.status)"
                      size="sm"
                      color="white"
                    />
                  </q-avatar>
                </q-item-section>
                <q-item-section>
                  <q-item-label class="text-subtitle1 ">
                    {{ translateCheckText(check.title) }}
                  </q-item-label>
                  <q-item-label caption>
                    {{ getCheckDescription(check) }}
                  </q-item-label>
                </q-item-section>
                <q-item-section
                  v-if="check.status === 'failed'"
                  side
                >
                  <div class="text-right">
                    <q-chip
                      dense
                      color="negative"
                      text-color="white"
                      class="q-mx-none q-px-sm"
                    >
                      {{ check.count }}
                    </q-chip>
                  </div>
                </q-item-section>
              </template>

              <!-- Check Details -->
              <q-card-section v-if="check.issues && check.issues.length > 0">
                <!-- Help message for failed checks -->
                <q-banner
                  v-if="check.status === 'failed'"
                  class="bg-surface q-mb-md p-4"
                  rounded
                >
                  <template v-slot:avatar>
                    <q-avatar
                      rounded
                      :style="{
                        backgroundColor: $q.dark.isActive ? 'rgba(255, 255, 255, 0.08)' : '#f2f2f2'
                      }"
                      :text-color="$q.dark.isActive ? 'white' : 'primary'"
                    >
                      <q-icon
                        name="help_outline"
                        size="md"
                      />
                    </q-avatar>
                  </template>
                  <div
                    class="text-secondary"
                    style="white-space: pre-line;"
                    v-html="getCheckHelp(check.name)"
                  />
                </q-banner>

                <div class="q-pa-none">
                  <!-- Negative Balances Issues -->
                  <template v-if="check.name === 'negative_balances'">
                    <q-markup-table
                      flat
                      class="full-width q-pa-none app-table"
                    >
                      <thead>
                        <tr>
                          <th
                            v-for="column in negativeBalancesColumns"
                            :key="column.name"
                            :class="`text-${column.align || 'left'}`"
                            :style="column.style"
                          >
                            {{ column.label }}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr
                          v-for="issue in getPaginatedIssues(check)"
                          :key="issue.movement_id"
                        >
                          <td class="text-left">
                            {{ formatDate(issue.date) }}
                          </td>
                          <td class="text-left">
                            {{ issue.exchange_asset_name }}
                          </td>
                          <td class="text-left">
                            {{ `${issue.asset_name} (${issue.asset_symbol})` }}
                          </td>
                          <td class="text-right">
                            <span class="price-negative">
                              {{ formatNumber(issue.balance) }}
                            </span>
                          </td>
                          <td class="text-right">
                            <q-btn
                              outline
                              dense
                              color="primary"
                              icon="filter_list"
                              size="sm"
                              :label="i18n.view_transactions"
                              @click="goToMovement(issue.movement_id)"
                            >
                              <q-tooltip>{{ i18n.view_transactions }}</q-tooltip>
                            </q-btn>
                          </td>
                        </tr>
                      </tbody>
                    </q-markup-table>
                    <div class="q-table__bottom row items-center justify-end">
                      <div class="q-table__control col-12">
                        <Pagination
                          :limits="paginationLimits"
                          :meta="getPaginationMeta(check)"
                          @click="(url, payload) => handlePaginationClick(check.name, url, payload)"
                        />
                      </div>
                    </div>
                  </template>

                  <!-- Internal Transfers Issues -->
                  <template v-if="check.name === 'internal_transfers'">
                    <q-markup-table
                      flat
                      class="full-width q-pa-none app-table"
                    >
                      <thead>
                        <tr>
                          <th
                            v-for="column in internalTransfersColumns"
                            :key="column.name"
                            :class="`text-${column.align || 'left'}`"
                            :style="column.style"
                          >
                            {{ column.label }}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr
                          v-for="issue in getPaginatedIssues(check)"
                          :key="issue.asset_id"
                        >
                          <td class="text-left">
                            {{ `${issue.asset_name} (${issue.asset_symbol})` }}
                          </td>
                          <td class="text-right">
                            <span :class="issue.sum > 0 ? 'price-positive' : 'price-negative'">
                              {{ formatNumber(issue.sum) }}
                            </span>
                          </td>
                          <td class="text-right">
                            <span class="text-warning">
                              {{ formatNumber(issue.difference) }}
                            </span>
                          </td>
                          <td class="text-right">
                            <q-btn
                              outline
                              dense
                              color="primary"
                              icon="filter_list"
                              size="sm"
                              :label="i18n.view_transactions"
                              @click="goToInternalTransfers(issue)"
                            >
                              <q-tooltip>{{ i18n.view_transactions }}</q-tooltip>
                            </q-btn>
                          </td>
                        </tr>
                      </tbody>
                    </q-markup-table>
                    <div class="q-table__bottom row items-center justify-end">
                      <div class="q-table__control col-12">
                        <Pagination
                          :limits="paginationLimits"
                          :meta="getPaginationMeta(check)"
                          @click="(url, payload) => handlePaginationClick(check.name, url, payload)"
                        />
                      </div>
                    </div>
                  </template>

                  <!-- Transaction Kinds Schema Issues -->
                  <template v-if="check.name === 'transaction_kinds_schemas'">
                    <q-markup-table
                      flat
                      class="full-width q-pa-none app-table"
                    >
                      <thead>
                        <tr>
                          <th
                            v-for="column in transactionKindsColumns"
                            :key="column.name"
                            :class="`text-${column.align || 'left'}`"
                            :style="column.style"
                          >
                            {{ column.label }}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr
                          v-for="issue in getPaginatedIssues(check)"
                          :key="issue.transaction_kind_id"
                        >
                          <td class="text-left">
                            {{ issue.transaction_kind_name }}
                          </td>
                          <td class="text-left">
                            {{ issue.transaction_kind_label }}
                          </td>
                          <td class="text-left">
                            {{ getMissingSchemaText(issue) }}
                          </td>
                          <td class="text-right">
                            <div class="row items-center q-gutter-xs justify-end">
                              <q-btn
                                v-if="isWorkspaceGroupAdmin"
                                outline
                                dense
                                color="primary"
                                icon="edit"
                                :label="i18n.configure"
                                size="sm"
                                @click="goToTransactionKindMapping(issue.transaction_kind_id)"
                              />
                              <q-btn
                                outline
                                dense
                                color="primary"
                                icon="filter_list"
                                size="sm"
                                :label="i18n.view_transactions"
                                @click="goToFilteredTransactions(issue)"
                              >
                                <q-tooltip>{{ i18n.view_transactions }}</q-tooltip>
                              </q-btn>
                            </div>
                          </td>
                        </tr>
                      </tbody>
                    </q-markup-table>
                    <div class="q-table__bottom row items-center justify-end">
                      <div class="q-table__control col-12">
                        <Pagination
                          :limits="paginationLimits"
                          :meta="getPaginationMeta(check)"
                          @click="(url, payload) => handlePaginationClick(check.name, url, payload)"
                        />
                      </div>
                    </div>
                  </template>
                </div>
              </q-card-section>
            </q-expansion-item>
            <q-item
              v-else
              class="q-px-md q-py-sm my-4"
            >
              <q-item-section avatar>
                <q-avatar
                  size="32px"
                  :color="getCheckColor(check.status)"
                >
                  <q-icon
                    :name="getCheckIcon(check.status)"
                    size="sm"
                    color="white"
                  />
                </q-avatar>
              </q-item-section>
              <q-item-section>
                <q-item-label class="text-subtitle1 ">
                  {{ translateCheckText(check.title) }}
                </q-item-label>
                <q-item-label caption>
                  {{ getCheckDescription(check) }}
                </q-item-label>
              </q-item-section>
            </q-item>
            <q-separator
              v-if="index < sortedChecks.length - 1"
              spaced
            />
          </template>
        </q-list>
      </div>

      <div v-else-if="placeholderChecks.length > 0">
        <q-list class="pt-4">
          <template
            v-for="(check, index) in placeholderChecks"
            :key="check.name"
          >
            <q-item class="q-px-md q-py-sm my-4">
              <q-item-section avatar>
                <q-avatar
                  size="32px"
                  class="bg-surface text-primary"
                >
                  <q-spinner
                    v-if="auditStatus === 'running'"
                    color="primary"
                    size="20px"
                  />
                  <q-icon
                    v-else
                    name="help_outline"
                    size="sm"
                    color="primary"
                  />
                </q-avatar>
              </q-item-section>
              <q-item-section>
                <q-item-label class="text-subtitle1 ">
                  {{ translateCheckText(check.title) }}
                </q-item-label>
                <q-item-label caption>
                  {{ getCheckDescription(check) }}
                </q-item-label>
              </q-item-section>
            </q-item>
            <q-separator
              v-if="index < placeholderChecks.length - 1"
              spaced
            />
          </template>
        </q-list>
      </div>

      <div
        v-else-if="auditStatus === 'idle'"
        class="text-center q-mt-xl"
      >
        <div class="mb-4 shadow inline-flex items-center justify-center flex flex-col gap-2 rounded-xl p-2">
          <q-icon
            name="checklist"
            size="28px"
            color="primary"
          />
        </div>
        <p class="text-primary font-medium text-md">
          {{ i18n.no_audit_yet }}
        </p>
        <q-btn
          color="primary"
          class="q-mt-sm"
          :loading="auditStatus === 'running'"
          :percentage="auditProgress"
          no-caps
          unelevated
          :disable="starting && auditStatus !== 'running'"
          :icon="auditStatus === 'running' ? 'hourglass_top' : 'auto_awesome'"
          :label="auditStatus === 'running' ? i18n.audit_running : i18n.start_audit"
          @click="startAudit"
        />
        <q-btn
          v-if="auditStatus === 'running' || auditStatus === 'pending'"
          color="negative"
          class="q-mt-sm q-ml-sm"
          outline
          no-caps
          unelevated
          :loading="cancelling"
          icon="stop_circle"
          :label="i18n.cancel_audit"
          @click="confirmCancelAudit"
        />
      </div>
    </div>
  </q-page>
</template>

<script>
import { defineComponent, ref, computed, onMounted, onUnmounted, reactive, watch } from 'vue'
import { router } from '@inertiajs/vue3'
import { useQuasar } from 'quasar'
import Layout from '@/layouts/workspace.vue'
import Pagination from '@/shared/pagination.vue'

export default defineComponent({
  name: 'AuditPage',
  components: {
    Pagination
  },
  layout: Layout,
  props: {
    audit_results: {
      type: Object,
      required: true
    },
    is_workspace_group_admin: {
      type: Boolean,
      default: false
    },
    is_professional: {
      type: Boolean,
      default: false
    },
    i18n: {
      type: Object,
      required: true
    }
  },
  setup(props) {
    const $q = useQuasar()
    const starting = ref(false)
    const cancelling = ref(false)
    const pollingInterval = ref(null)

    // Reactive audit data
    const auditData = ref(props.audit_results)
    const isWorkspaceGroupAdmin = ref(props.is_workspace_group_admin)
    const isProfessional = ref(props.is_professional)
    const auditStatus = computed(() => auditData.value.status || 'idle')
    const auditProgress = computed(() => auditData.value.progress || 0)
    const checkTemplates = [
      {
        name: 'negative_balances',
        title: 'workspace_audit.checks.negative_balances.title',
        description: 'workspace_audit.checks.negative_balances.description'
      },
      {
        name: 'internal_transfers',
        title: 'workspace_audit.checks.internal_transfers.title',
        description: 'workspace_audit.checks.internal_transfers.description'
      },
      {
        name: 'transaction_kinds_schemas',
        title: 'workspace_audit.checks.transaction_kinds_schemas.title',
        description: 'workspace_audit.checks.transaction_kinds_schemas.description'
      }
    ]
    const visibleCheckTemplates = computed(() => {
      if (isProfessional.value) {
        return checkTemplates
      }
      return checkTemplates.filter(check => check.name !== 'transaction_kinds_schemas')
    })
    watch(() => props.audit_results, (val) => {
      auditData.value = val
    })
    // Computed property to ensure checks are always in the same order
    const sortedChecks = computed(() => {
      if (!auditData.value.checks || auditData.value.checks.length === 0) {
        return []
      }

      // Define the desired order
      const checkOrder = visibleCheckTemplates.value.reduce((order, check, index) => {
        order[check.name] = index + 1
        return order
      }, {})

      // Filter out transaction_kinds_schemas check for non-professional workspaces
      let checks = auditData.value.checks
      if (!isProfessional.value) {
        checks = checks.filter(check => check.name !== 'transaction_kinds_schemas')
      }

      // Sort checks based on the defined order
      return [...checks].sort((a, b) => {
        const orderA = checkOrder[a.name] || 999
        const orderB = checkOrder[b.name] || 999
        return orderA - orderB
      })
    })
    const placeholderChecks = computed(() => {
      if (sortedChecks.value.length > 0) {
        return []
      }
      if (auditStatus.value === 'running' || auditStatus.value === 'pending') {
        return visibleCheckTemplates.value
      }
      return []
    })

    const auditMessage = computed(() => {
      const msg = auditData.value.message || ''
      // If message is a translation key, translate it
      if (msg.startsWith('workspace_audit.progress.')) {
        // Check if it's an error message with additional data
        if (msg.startsWith('workspace_audit.progress.error:')) {
          const errorMsg = msg.replace('workspace_audit.progress.error:', '')
          return props.i18n.error.replace('%{error}', errorMsg)
        }
        // Map the key to the translation
        const key = msg.replace('workspace_audit.progress.', '')
        const translations = {
          'starting': props.i18n.starting,
          'calculating_wac': props.i18n.calculating_wac,
          'wac_completed': props.i18n.wac_completed,
          'generating_fec': props.i18n.generating_fec,
          'fec_completed': props.i18n.fec_completed,
          'checking_balances': props.i18n.checking_balances,
          'balances_checked': props.i18n.balances_checked,
          'checking_transfers': props.i18n.checking_transfers,
          'transfers_checked': props.i18n.transfers_checked,
          'checking_schemas': props.i18n.checking_schemas,
          'schemas_checked': props.i18n.schemas_checked,
          'completed': props.i18n.completed
        }
        return translations[key] || msg
      }
      return msg
    })
    const auditStatusText = computed(() => {
      if (auditMessage.value) {
        return auditMessage.value
      }
      if (auditStatus.value === 'running' || auditStatus.value === 'pending') {
        return props.i18n.audit_in_progress
      }
      if (auditStatus.value === 'idle') {
        return props.i18n.no_audit_yet
      }
      return ''
    })
    const errorMessage = ref('')
    const formattedError = computed(() => {
      if (errorMessage.value) {
        return props.i18n.error.replace('%{error}', errorMessage.value)
      }

      const msg = auditData.value.message || ''
      if (msg.startsWith('workspace_audit.progress.error:')) {
        const rawError = msg.replace('workspace_audit.progress.error:', '')
        return props.i18n.error.replace('%{error}', rawError)
      }

      return auditMessage.value
    })

    const negativeBalancesColumns = [
      { name: 'date', label: props.i18n.date, field: 'date', align: 'left' },
      { name: 'exchange_asset', label: props.i18n.exchange_asset, field: 'exchange_asset_name', align: 'left' },
      { name: 'asset', label: props.i18n.asset, field: row => `${row.asset_name} (${row.asset_symbol})`, align: 'left' },
      { name: 'balance', label: props.i18n.balance, field: 'balance', align: 'right' },
      { name: 'actions', label: props.i18n.actions, align: 'right' }
    ]

    const internalTransfersColumns = [
      { name: 'asset', label: props.i18n.asset, field: row => `${row.asset_name} (${row.asset_symbol})`, align: 'left' },
      { name: 'sum', label: props.i18n.sum, field: 'sum', align: 'right' },
      { name: 'difference', label: props.i18n.absolute_difference, field: 'difference', align: 'right' },
      { name: 'actions', label: props.i18n.actions, align: 'right' }
    ]

    const transactionKindsColumns = [
      { name: 'name', label: props.i18n.technical_name, field: 'transaction_kind_name', align: 'left' },
      { name: 'label', label: props.i18n.label, field: 'transaction_kind_label', align: 'left' },
      { name: 'debit_credit', label: props.i18n.debit_credit, field: row => getMissingSchemaText(row), align: 'left' },
      { name: 'actions', label: props.i18n.actions, align: 'right' }
    ]

    const DEFAULT_ROWS_PER_PAGE = 10
    const paginationLimits = [5, 10, 20, 50]
    const paginationState = reactive({})

    const ensurePaginationState = (checkName) => {
      if (!paginationState[checkName]) {
        paginationState[checkName] = {
          page: 1,
          perPage: DEFAULT_ROWS_PER_PAGE
        }
      }
      return paginationState[checkName]
    }

    const getPaginationData = (check) => {
      const issues = Array.isArray(check?.issues) ? check.issues : []
      const state = ensurePaginationState(check?.name)
      const perPage = state.perPage > 0 ? state.perPage : DEFAULT_ROWS_PER_PAGE
      const lastPage = Math.max(1, Math.ceil(issues.length / perPage))
      const currentPage = Math.min(Math.max(1, state.page || 1), lastPage)

      if (currentPage !== state.page) {
        state.page = currentPage
      }

      const startIndex = (currentPage - 1) * perPage
      const rows = issues.slice(startIndex, startIndex + perPage)
      const sequels = Array.from({ length: lastPage }, (_, index) => index + 1)

      return {
        rows,
        meta: {
          scaffold_url: '#?page=__pagy_page__&per_page=__pagy_limit__',
          count: issues.length,
          page: currentPage,
          prev: currentPage > 1 ? currentPage - 1 : 1,
          next: currentPage < lastPage ? currentPage + 1 : lastPage,
          last: lastPage,
          sequels: { '0': sequels },
          limit: perPage
        }
      }
    }

    const getPaginatedIssues = (check) => getPaginationData(check).rows
    const getPaginationMeta = (check) => getPaginationData(check).meta
    const handlePaginationClick = (checkName, _url, payload) => {
      const state = ensurePaginationState(checkName)
      if (payload?.per_page) {
        state.perPage = payload.per_page
      }
      state.page = payload?.page || 1
    }

    const getCheckIcon = (status) => {
      switch (status) {
      case 'passed': return 'sym_o_check'
      case 'failed': return 'sym_o_exclamation'
      case 'pending': return 'sym_o_warning'
      default: return 'help'
      }
    }

    const getCheckColor = (status) => {
      switch (status) {
      case 'passed': return 'positive'
      case 'failed': return 'negative'
      case 'pending': return 'warning'
      default: return 'grey'
      }
    }

    const getCheckHeaderClass = (status) => {
      switch (status) {
      case 'passed': return ''
      case 'failed': return 'bg-negative-1'
      case 'pending': return 'bg-warning-1'
      default: return ''
      }
    }

    const formatDate = (date) => {
      if (!date) return '-'

      // Handle different date formats
      let dateObj
      if (typeof date === 'string') {
        dateObj = new Date(date)
      } else if (date && typeof date === 'object' && date.utc) {
        // Handle Temporal's serialized format
        if (date.utc['^t']) {
          dateObj = new Date(date.utc['^t'] * 1000)
        } else {
          return '-'
        }
      } else if (date && typeof date === 'object' && date['^t']) {
        // Handle simple timestamp format
        dateObj = new Date(date['^t'] * 1000)
      } else {
        dateObj = new Date(date)
      }

      // Check if date is valid
      if (isNaN(dateObj.getTime())) {
        console.error('Invalid date:', date)
        return '-'
      }

      return dateObj.toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    }

    const formatNumber = (num) => {
      if (num === null || num === undefined) return '-'
      return new Intl.NumberFormat('fr-FR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 8
      }).format(num)
    }

    const translateCheckText = (text) => {
      if (!text) return ''

      // Map translation keys to actual translations
      const translationMap = {
        'workspace_audit.checks.negative_balances.title': props.i18n.checks_negative_balances_title,
        'workspace_audit.checks.negative_balances.description': props.i18n.checks_negative_balances_description,
        'workspace_audit.checks.negative_balances.success_description': props.i18n.checks_negative_balances_success_description,
        'workspace_audit.checks.internal_transfers.title': props.i18n.checks_internal_transfers_title,
        'workspace_audit.checks.internal_transfers.description': props.i18n.checks_internal_transfers_description,
        'workspace_audit.checks.internal_transfers.success_description': props.i18n.checks_internal_transfers_success_description,
        'workspace_audit.checks.transaction_kinds_schemas.title': props.i18n.checks_transaction_kinds_schemas_title,
        'workspace_audit.checks.transaction_kinds_schemas.description': props.i18n.checks_transaction_kinds_schemas_description,
        'workspace_audit.checks.transaction_kinds_schemas.success_description': props.i18n.checks_transaction_kinds_schemas_success_description
      }

      return translationMap[text] || text
    }

    const successDescriptionKeys = {
      negative_balances: 'workspace_audit.checks.negative_balances.success_description',
      internal_transfers: 'workspace_audit.checks.internal_transfers.success_description',
      transaction_kinds_schemas: 'workspace_audit.checks.transaction_kinds_schemas.success_description'
    }

    const getCheckDescriptionKey = (check) => {
      if (check?.status === 'passed') {
        return successDescriptionKeys[check.name] || check?.description || ''
      }
      return check?.description || ''
    }

    const getCheckDescription = (check) => translateCheckText(getCheckDescriptionKey(check))

    const getCheckHelp = (checkName) => {
      switch (checkName) {
      case 'negative_balances':
        return props.i18n.help_negative_balances
      case 'internal_transfers':
        return props.i18n.help_internal_transfers
      case 'transaction_kinds_schemas':
        return isWorkspaceGroupAdmin.value
          ? props.i18n.help_transaction_kinds_schemas
          : props.i18n.help_transaction_kinds_schemas_non_admin
      default:
        return ''
      }
    }

    const getMissingSchemaText = (row) => {
      if (row.missing_debit && row.missing_credit) {
        return props.i18n.missing_both
      } else if (row.missing_debit) {
        return props.i18n.missing_debit
      } else if (row.missing_credit) {
        return props.i18n.missing_credit
      }
      return ''
    }

    const startAudit = () => {
      starting.value = true
      errorMessage.value = ''

      const workspaceId = props.audit_results?.workspace_id || auditData.value?.workspace_id
      if (!workspaceId) {
        console.error('No workspace_id found in audit_results')
        errorMessage.value = props.i18n.error_missing_workspace
        starting.value = false
        return
      }

      router.post(`/w/${workspaceId}/audit`, {}, {
        onStart: () => {
          // Update the local status immediately
          auditData.value.status = 'running'
          auditData.value.progress = 0
          auditData.value.message = props.i18n.starting_audit_message
        },
        onSuccess: () => {
          // After redirect, start polling immediately
          if (auditStatus.value === 'running' || auditStatus.value === 'idle') {
            $q.notify({
              type: 'info',
              message: props.i18n.audit_launched
            })
            startPolling()
          }
        },
        onFinish: () => {
          starting.value = false
        }
      })
    }

    const confirmCancelAudit = () => {
      $q.dialog({
        title: props.i18n.cancel_audit_confirm_title,
        message: props.i18n.cancel_audit_confirm_message,
        cancel: {
          label: props.i18n.cancel_audit_confirm_cancel,
          flat: true,
          noCaps: true
        },
        ok: {
          label: props.i18n.cancel_audit_confirm_ok,
          color: 'negative',
          unelevated: true,
          noCaps: true
        },
        persistent: true
      }).onOk(() => {
        cancelAudit()
      })
    }

    const cancelAudit = () => {
      const workspaceId = props.audit_results?.workspace_id || auditData.value?.workspace_id
      if (!workspaceId) {
        errorMessage.value = props.i18n.error_missing_workspace
        return
      }

      cancelling.value = true
      // Stop polling right away: the audit runs are about to be deleted, so
      // further status calls would only report the idle state.
      stopPolling()

      router.delete(`/w/${workspaceId}/audit/cancel`, {
        onSuccess: () => {
          auditData.value.status = 'idle'
          auditData.value.progress = 0
          auditData.value.message = ''
          errorMessage.value = ''
        },
        onError: (errors) => {
          console.error(errors)
          $q.notify({
            type: 'negative',
            message: props.i18n.error ? props.i18n.error.replace('%{error}', '') : 'Error'
          })
        },
        onFinish: () => {
          cancelling.value = false
        }
      })
    }

    const startPolling = () => {
      // Clear any existing interval
      if (pollingInterval.value) {
        clearInterval(pollingInterval.value)
      }

      const workspaceId = props.audit_results?.workspace_id || auditData.value?.workspace_id
      if (!workspaceId) {
        console.error('No workspace_id for polling')
        return
      }

      // Poll every 2 seconds
      pollingInterval.value = setInterval(async () => {
        try {
          const response = await fetch(`/w/${workspaceId}/audit/status`, {
            headers: {
              'Accept': 'application/json',
              'Content-Type': 'application/json'
            }
          })

          const data = await response.json()

          // Update audit data
          if (data.results) {
            auditData.value = data.results
          }
          auditData.value.status = data.status
          auditData.value.progress = data.progress
          auditData.value.message = data.message

          // Stop polling if completed or failed
          if (data.status === 'completed' || data.status === 'failed') {
            stopPolling()

            if (data.status === 'completed') {
              $q.notify({
                type: 'positive',
                message: 'Audit terminé avec succès'
              })
            } else if (data.status === 'failed') {
              errorMessage.value = data.error || 'Erreur inconnue'
              $q.notify({
                type: 'negative',
                message: 'L\'audit a échoué'
              })
            }
          }
        } catch (error) {
          console.error('Polling error:', error)
        }
      }, 2000)
    }

    const stopPolling = () => {
      if (pollingInterval.value) {
        clearInterval(pollingInterval.value)
        pollingInterval.value = null
      }
    }

    const goToMovement = (movementId) => {
      router.visit(`/w/${props.audit_results.workspace_id}/transactions?id=${movementId}`)
    }

    const goToInternalTransfers = (issue) => {
      // Navigate to transactions filtered by internal_transfer transaction kind and date range
      const params = new URLSearchParams()

      // Add transaction_kind filter if we have the ID
      if (issue.transaction_kind_id) {
        params.append('kind[]', issue.transaction_kind_id)
      }

      // Add date filter if we have dates
      // Add padding of 1 day before and after to account for timezone differences
      if (issue.first_date && issue.last_date) {
        const formatDateForUrl = (date, dayOffset = 0) => {
          const d = new Date(date)
          d.setDate(d.getDate() + dayOffset)
          // Get local date string in YYYY-MM-DD format
          const year = d.getFullYear()
          const month = String(d.getMonth() + 1).padStart(2, '0')
          const day = String(d.getDate()).padStart(2, '0')
          return `${year}-${month}-${day}`
        }

        // Add 1 day padding before first date and after last date for timezone safety
        const startDate = formatDateForUrl(issue.first_date, -1)
        const endDate = formatDateForUrl(issue.last_date, 1)

        // Add date range parameters
        params.append('date_from', startDate)
        params.append('date_to', endDate)
      }

      // Add asset filter
      if (issue.asset_id) {
        params.append('asset_id[]', issue.asset_id)
      }

      router.visit(`/w/${props.audit_results.workspace_id}/transactions?${params.toString()}`)
    }

    const goToTransactionKindMapping = (transactionKindId) => {
      // Navigate to the accounting mappings page with the transaction kind filter
      // This is the same as clicking on a transaction in the accounting details tab
      router.visit(`/wg_admin/${props.audit_results.workspace_group_id}/accounting_plans/${props.audit_results.accounting_plan_id}/accounting_mappings?transaction_kind_id=${transactionKindId}`)
    }

    const goToFilteredTransactions = (issue) => {
      // Navigate to transactions filtered by transaction kind and type (in/out)
      const params = new URLSearchParams()

      // Add transaction kind filter
      params.append('kind[]', issue.transaction_kind_id)

      // Add type filter based on what's missing
      // If missing_credit (positive amounts) -> type=in
      // If missing_debit (negative amounts) -> type=out
      // If both are missing, show the one that exists
      if (issue.missing_credit && !issue.missing_debit) {
        params.append('type', 'in')
      } else if (issue.missing_debit && !issue.missing_credit) {
        params.append('type', 'out')
      } else if (issue.missing_credit && issue.missing_debit) {
        // Both are missing, show the one that has transactions
        if (issue.has_positive && !issue.has_negative) {
          params.append('type', 'in')
        } else if (issue.has_negative && !issue.has_positive) {
          params.append('type', 'out')
        }
        // If both have transactions, don't filter by type
      }

      router.visit(`/w/${props.audit_results.workspace_id}/transactions?${params.toString()}`)
    }

    // Start polling if audit is already running or check for running workflow
    onMounted(() => {
      if (auditStatus.value === 'running') {
        $q.notify({
          type: 'info',
          message: props.i18n.audit_in_progress
        })
        startPolling()
      } else if (auditStatus.value === 'idle' || !auditStatus.value) {
        // Check once to see if there's a running workflow
        const workspaceId = props.audit_results?.workspace_id || auditData.value?.workspace_id
        if (workspaceId) {
          fetch(`/w/${workspaceId}/audit/status`, {
            headers: {
              'Accept': 'application/json',
              'Content-Type': 'application/json'
            }
          })
            .then(response => response.json())
            .then(data => {
              if (data.status === 'running') {
                auditData.value.status = 'running'
                auditData.value.progress = data.progress || 0
                auditData.value.message = data.message || props.i18n.audit_in_progress
                startPolling()
              } else if (data.status === 'completed' && data.results) {
                // Update with completed results
                auditData.value = { ...auditData.value, ...data.results }
                auditData.value.status = 'completed'
              }
            })
            .catch(error => {
              console.error('Failed to check audit status:', error)
            })
        }
      }
    })

    // Clean up on unmount
    onUnmounted(() => {
      stopPolling()
    })

    return {
      starting,
      cancelling,
      auditData,
      sortedChecks,
      placeholderChecks,
      auditStatus,
      auditProgress,
      auditMessage,
      auditStatusText,
      errorMessage,
      formattedError,
      isWorkspaceGroupAdmin,
      negativeBalancesColumns,
      internalTransfersColumns,
      transactionKindsColumns,
      paginationLimits,
      getPaginatedIssues,
      getPaginationMeta,
      handlePaginationClick,
      getCheckIcon,
      getCheckColor,
      getCheckHeaderClass,
      formatDate,
      formatNumber,
      translateCheckText,
      getCheckDescription,
      getCheckHelp,
      getMissingSchemaText,
      startAudit,
      confirmCancelAudit,
      goToMovement,
      goToInternalTransfers,
      goToTransactionKindMapping,
      goToFilteredTransactions
    }
  }
})
</script>

<style scoped>
.bg-negative-1 {
  background-color: rgba(255, 0, 0, 0.05);
}

.bg-warning-1 {
  background-color: rgba(255, 193, 7, 0.05);
}
</style>
